import { DocumentRowContextMenu } from "@/domains/documents/components/organisms/DocumentRowContextMenu";
import { DocumentTableRow } from "@/domains/documents/components/organisms/DocumentTableRow";
import { isIfcFile } from "@/domains/documents/constants/fileTypes";
import { documentQuickViewKind, isDwgParentDoc } from "@/domains/documents/hooks/useDrawingViewer";
import type { DocumentState } from "@/types/documents";
import type { VirtualItem } from "@tanstack/react-virtual";
import { observer } from "mobx-react-lite";
import type { ComponentProps } from "react";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import type { DocumentWithTags } from "./documentTableTypes";
import { verifyMarks } from "./documentTableValues";
import type { DocumentTableState } from "./useDocumentTableState";
import type { FlattenedItem } from "./useTableRows";
type DocumentContentsProps = DocumentTablePresentation & {
  doc: DocumentWithTags;
  isExpandedVersion: boolean;
  parentDoc:
    | ReturnType<NonNullable<DocumentTableState["store"]>["documents"]["get"]>
    | null
    | undefined;
  item: FlattenedItem;
  isVersionExpanded: boolean;
  rowUnifiedIndex: number | undefined;
  quickViewKind: ReturnType<typeof documentQuickViewKind>;
};
export const TableDocumentContents = observer(function TableDocumentContents(
  props: DocumentContentsProps
) {
  const { documentMoveProps, doc, isExpandedVersion } = props;
  return (
    <DocumentTableRow
      {...documentMoveProps(doc, isExpandedVersion)}
      {...documentRowData(props)}
      {...documentRowLocation(props)}
      {...documentRowAppearance(props)}
      {...documentRowColumns(props)}
      {...documentRowVersions(props)}
      // Older IFC and DWG versions are individually
      // selectable so two versions of one file can be
      // compared. Gated per ROW, not on the group's anchor
      // filename: a version group is not extension-locked
      // (a .pdf revision can follow a .dwg), so the anchor's
      // extension would tick the wrong rows either way.

      {...documentRowSelection(props)}
      {...documentRowFiles(props)}
      {...documentRowTags(props)}
      {...documentRowSearch(props)}
      {...documentRowRename(props)}
      {...documentRowVerification(props)}
      {...documentRowHostActions(props)}
    />
  );
});

export const TableDocumentRow = observer(function TableDocumentRow(
  props: DocumentTablePresentation & { item: FlattenedItem; virtualRow: VirtualItem }
) {
  const {
    item,
    store,
    versionExpansion,
    unifiedIndexById,
    canUseDrawingViewer,
    isRestore,
    virtualRow,
    documentRename,
    buildDocMenuConfig,
    measureVirtualRow,
  } = props;

  // Every other kind returned above. Narrowed explicitly rather
  // than assumed, so a row kind added later is a type error here
  // instead of a container silently rendered as a document row.
  if (item.kind !== "doc") return null;

  const doc = item.doc;
  const isExpandedVersion = item.isExpandedVersion;
  // Look up parent document filename if parent_document_id exists
  const parentDoc = doc.parent_document_id ? store?.documents.get(doc.parent_document_id) : null;
  const vg = doc.version_group;
  const isVersionExpanded = !!vg && versionExpansion.isExpanded(vg);
  const rowUnifiedIndex = unifiedIndexById.get(`document:${doc.id}`);
  const quickViewKind = documentQuickViewKind(doc, canUseDrawingViewer, isRestore);

  return (
    <DocumentRowContextMenu
      key={virtualRow.key}
      variant="document"
      disabled={isExpandedVersion || documentRename.isEditing(doc.id) || !store}
      {...buildDocMenuConfig(doc)}
    >
      <div
        data-index={virtualRow.index}
        ref={measureVirtualRow}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualRow.start}px)`,
        }}
      >
        <TableDocumentContents
          {...props}
          doc={doc}
          isExpandedVersion={isExpandedVersion}
          parentDoc={parentDoc}
          item={item}
          isVersionExpanded={isVersionExpanded}
          rowUnifiedIndex={rowUnifiedIndex}
          quickViewKind={quickViewKind}
        />
      </div>
    </DocumentRowContextMenu>
  );
});

function documentRowData(
  props: DocumentContentsProps
): Pick<ComponentProps<typeof DocumentTableRow>, "document"> {
  const { doc } = props;
  return {
    document: {
      id: doc.id,
      filename: doc.filename,
      content_type: doc.content_type,
      size: doc.size,
      created_at: doc.created_at,
      original_created_at: doc.original_created_at,
      modified_at: doc.modified_at,
      document_content_modified_date: doc.document_content_modified_date,
      created_by: doc.created_by,
      created_by_name: doc.created_by_name,
      created_by_avatar_url: doc.created_by_avatar_url,
      created_by_color: doc.created_by_color,
      verified_at: doc.verified_at ?? null,
      verified_by_name: doc.verified_by_name ?? null,
      state: doc.state as DocumentState | null,
      version: doc.version,
      version_group: doc.version_group,
      parent_document_id: doc.parent_document_id,
    },
  };
}

function documentRowLocation(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  "parentDocumentName" | "folderPath" | "breadcrumbSegments" | "buildingId"
> {
  const { parentDoc, documentFolderPaths, doc, item, documentBreadcrumbs, buildingId } = props;
  return {
    parentDocumentName: parentDoc?.filename,

    folderPath: documentFolderPaths.get(doc.id),

    breadcrumbSegments:
      item.kind === "doc" && item.breadcrumbSegments !== undefined
        ? (item.breadcrumbSegments ?? undefined)
        : documentBreadcrumbs.get(doc.id),

    buildingId: buildingId,
  };
}

function documentRowAppearance(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  "isSelected" | "tags" | "linkedIssues" | "className"
> {
  const { store, doc, documentTagsMap, isExpandedVersion, resolveTags } = props;
  return {
    isSelected: store?.isSelected(doc.id) ?? false,

    tags: documentTagsMap.get(doc.id) ?? (isExpandedVersion ? resolveTags(doc) : []),

    linkedIssues: doc.linked_issues,

    className: isExpandedVersion
      ? "duration-150 ease-out animate-in fade-in-0 slide-in-from-top-1"
      : undefined,
  };
}

function documentRowColumns(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  | "showCheckbox"
  | "showVersion"
  | "showDateCreated"
  | "showStatus"
  | "showUser"
  | "showTags"
  | "showParent"
  | "showActions"
  | "showDownloadAction"
  | "showDeleteAction"
  | "deleteLabel"
  | "isRestore"
> {
  const {
    showCheckbox,
    showVersion,
    showDateCreated,
    showStatus,
    showUser,
    showTags,
    showParent,
    showActions,
    showDownloadAction,
    showDeleteAction,
    deleteLabel,
    isRestore,
  } = props;
  return {
    showCheckbox: showCheckbox,

    showVersion: showVersion,

    showDateCreated: showDateCreated,

    showStatus: showStatus,

    showUser: showUser,

    showTags: showTags,

    showParent: showParent,

    showActions: showActions,

    showDownloadAction: showDownloadAction,

    showDeleteAction: showDeleteAction,

    deleteLabel: deleteLabel,

    isRestore: isRestore,
  };
}

function documentRowVersions(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  | "isExpandedVersion"
  | "versionSelectable"
  | "isVersionExpanded"
  | "onShowVersionHistory"
  | "onAddNewVersion"
> {
  const {
    isExpandedVersion,
    doc,
    isVersionExpanded,
    versionExpansion,
    onAddNewVersions,
    addVersions,
  } = props;
  return {
    isExpandedVersion: isExpandedVersion,

    // Older IFC and DWG versions are individually
    // selectable so two versions of one file can be
    // compared. Gated per ROW, not on the group's anchor
    // filename: a version group is not extension-locked
    // (a .pdf revision can follow a .dwg), so the anchor's
    // extension would tick the wrong rows either way.
    versionSelectable: isIfcFile(doc.filename) || isDwgParentDoc(doc),

    isVersionExpanded: isVersionExpanded,

    onShowVersionHistory:
      !isExpandedVersion && doc.version_group
        ? () => versionExpansion.toggle(doc.version_group, doc.id)
        : undefined,

    onAddNewVersion:
      onAddNewVersions && !isExpandedVersion ? () => addVersions.openFor(doc) : undefined,
  };
}

function documentRowSelection(
  props: DocumentContentsProps
): Pick<ComponentProps<typeof DocumentTableRow>, "onSelect" | "onRowClickCapture"> {
  const { handleSelectDocument, doc, isExpandedVersion, rowUnifiedIndex, handleRowClickCapture } =
    props;
  return {
    onSelect: (isSelected) => handleSelectDocument(doc.id, isSelected),

    onRowClickCapture:
      isExpandedVersion || rowUnifiedIndex === undefined
        ? undefined
        : handleRowClickCapture(doc.id, rowUnifiedIndex),
  };
}

function documentRowFiles(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  | "onDownload"
  | "onPreview"
  | "onDelete"
  | "onDownloadParent"
  | "onOpenDrawingViewer"
  | "onShowChildren"
> {
  const {
    handleDownloadDocument,
    doc,
    quickViewKind,
    openDrawingViewer,
    handlePreviewDocument,
    canMoveToTrash,
    handleDeleteDocument,
    handleDownloadParent,
    isRestore,
    canUseDrawingViewer,
    disableChildModal,
    handleShowChildren,
  } = props;
  return {
    onDownload: () => handleDownloadDocument(doc.id),

    onPreview:
      quickViewKind === "drawing"
        ? () => openDrawingViewer([doc.id])
        : quickViewKind === "preview"
          ? (activatingControl) => handlePreviewDocument(doc.id, activatingControl)
          : undefined,

    onDelete: canMoveToTrash ? () => handleDeleteDocument(doc.id) : undefined,

    onDownloadParent: handleDownloadParent,

    onOpenDrawingViewer:
      !isRestore && canUseDrawingViewer ? () => openDrawingViewer([doc.id]) : undefined,

    onShowChildren: !disableChildModal ? () => handleShowChildren(doc.id, doc.filename) : undefined,
  };
}

function documentRowTags(
  props: DocumentContentsProps
): Pick<ComponentProps<typeof DocumentTableRow>, "onTeachFranklin" | "onTagRemove"> {
  const { store, handleTeachFranklin, doc, handleTagRemove } = props;
  return {
    onTeachFranklin: store ? () => handleTeachFranklin(doc) : undefined,

    onTagRemove: store ? (tag) => handleTagRemove(doc.id, tag) : undefined,
  };
}

function documentRowSearch(
  props: DocumentContentsProps
): Pick<ComponentProps<typeof DocumentTableRow>, "isContentMatch" | "contentMatchInfo"> {
  const { store, doc } = props;
  return {
    isContentMatch: documentContentMatched(props),

    contentMatchInfo: documentMatchInfo(props),
  };
}

function documentRowRename(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  | "onRename"
  | "isRenaming"
  | "renameBasename"
  | "renameExtension"
  | "onRenameChange"
  | "onRenameConfirm"
  | "onRenameCancel"
> {
  const { onRenameDocumentSubmit, isExpandedVersion, canRenameDocument, doc, documentRename } =
    props;
  return {
    onRename:
      onRenameDocumentSubmit && !isExpandedVersion && (canRenameDocument?.(doc) ?? false)
        ? () =>
            documentRename.startRename({
              id: doc.id,
              filename: doc.filename,
            })
        : undefined,

    isRenaming: documentRename.isEditing(doc.id),

    renameBasename: documentRename.editBasename,

    renameExtension: documentRename.extension,

    onRenameChange: documentRename.setEditBasename,

    onRenameConfirm: () => {
      void documentRename.confirmRename();
    },

    onRenameCancel: documentRename.cancelRename,
  };
}

function documentRowVerification(
  props: DocumentContentsProps
): Pick<
  ComponentProps<typeof DocumentTableRow>,
  "onToggleVerified" | "onComments" | "commentCounts"
> {
  const {
    isRestore,
    isExpandedVersion,
    canVerifyDocument,
    doc,
    setVerified,
    canOpenComments,
    openComments,
    commentCounts,
  } = props;
  return {
    onToggleVerified:
      !isRestore && !isExpandedVersion && canVerifyDocument(doc)
        ? () => void setVerified(verifyMarks([doc]), doc.verified_at == null)
        : undefined,

    onComments: canOpenComments && !isExpandedVersion ? () => openComments(doc) : undefined,

    commentCounts: commentCounts.get(Number(doc.id)),
  };
}

function documentRowHostActions(
  props: DocumentContentsProps
): Pick<ComponentProps<typeof DocumentTableRow>, "hostAction" | "onMoveCopy"> {
  const { hostAction, isExpandedVersion, doc, onMoveCopy, store } = props;
  return {
    hostAction:
      hostAction && !isExpandedVersion
        ? {
            label: hostAction.label,
            icon: hostAction.icon,
            onClick: () => hostAction.onRun([{ id: doc.id, filename: doc.filename }]),
          }
        : undefined,

    onMoveCopy:
      onMoveCopy && !isExpandedVersion
        ? () => {
            // Mirror the bulk Move/Copy action from the row's
            // 3-dots menu: select just this doc so the existing
            // bulk handler operates on a single-row "selection",
            // then open the picker (WHA-3253).
            store?.clearSelection();
            store?.selectDocument(doc.id, true);
            onMoveCopy();
          }
        : undefined,
  };
}

function documentContentMatched({ store, doc }: DocumentContentsProps) {
  return store?.isContentMatch?.(doc.id) ?? false;
}
function documentMatchInfo({ store, doc }: DocumentContentsProps) {
  return store?.getContentMatchInfo?.(doc.id) ?? null;
}
